// Clickable maps for the detective game, one per chapter (see Detective.js).
// Each takes: current (location id), onSelect(id), isOpen(id), names { id: label }, countAt(id).
import React from 'react';
import styles from './Detective.module.css';

const mapHelpers = ({ current, onSelect, isOpen, names, countAt }) => {
  const room = (id) => {
    if (!isOpen(id)) return { className: styles.roomLocked, 'aria-hidden': true };
    return {
      className: `${styles.room} ${current === id ? styles.roomActive : ''}`,
      onClick: () => onSelect(id),
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(id);
        }
      },
      role: 'button',
      tabIndex: 0,
      'aria-label': names[id],
    };
  };

  // One dot per person waiting in that room.
  const people = (id, x, y) => (isOpen(id) ? Array.from({ length: countAt(id) }, (_, i) => (
    <circle key={i} className={styles.person} cx={x + i * 14} cy={y} r="4.5" />
  )) : null);

  // Places away from the main site: stacked down the right side, hidden until the case leads there.
  const outside = (id, y) => (
    <g {...room(id)}>
      <rect x="492" y={y} width="204" height="58" />
      <text className={styles.roomLabel} x="594" y={y + 28}>{isOpen(id) ? names[id] : '· · ·'}</text>
      {people(id, 587, y + 44)}
    </g>
  );

  // A place drawn as one labelled box; the dots of whoever is there sit inside it,
  // so the box you see is exactly the box you can press.
  const node = (id, x, y, label = names[id]) => {
    const busy = isOpen(id) && countAt(id) > 0;
    return (
      <g {...room(id)}>
        <rect x={x} y={y} width="136" height="54" rx="6" />
        <text className={styles.roomLabel} x={x + 68} y={y + (busy ? 25 : 34)}>{isOpen(id) ? label : '· · ·'}</text>
        {people(id, x + 68 - 7 * (countAt(id) - 1), y + 42)}
      </g>
    );
  };

  return { room, people, outside, node };
};

export function LabFloorMap(props) {
  const { room, people } = mapHelpers(props);

  return (
    <svg className={styles.map} viewBox="0 0 720 340" role="group" aria-label="자연과학관 평면도">
      <text className={styles.floorTag} x="24" y="15">자연과학관 4층</text>

      {/* corridor, with the camera that only watches the front doors */}
      <rect className={styles.corridor} x="24" y="204" width="672" height="40" />
      <text className={styles.corridorLabel} x="470" y="229">복도</text>
      <path className={styles.cameraCone} d="M262 224 L52 208 L52 240 Z" />
      <rect className={styles.camera} x="258" y="219" width="12" height="10" rx="2" />
      <text className={styles.tiny} x="276" y="229">CCTV</text>

      <g {...room('office')}>
        <rect x="24" y="24" width="150" height="180" />
        <line className={styles.window} x1="40" y1="24" x2="150" y2="24" />
        <rect className={styles.decor} x="36" y="34" width="34" height="18" />
        <circle className={styles.flower} cx="53" cy="43" r="3" />
        <rect className={styles.decor} x="80" y="34" width="34" height="18" />
        <rect className={styles.decor} x="124" y="34" width="34" height="18" />
        <rect className={styles.decor} x="36" y="150" width="34" height="18" />
        <rect className={styles.decor} x="80" y="150" width="34" height="18" />
        <rect className={styles.decor} x="124" y="150" width="34" height="18" />
        <text className={styles.roomLabel} x="99" y="108">학생실</text>
        {people('office', 86, 128)}
      </g>

      <g {...room('lab')}>
        <path d="M174 24 H314 V102 H404 V204 H174 Z" />
        <rect className={styles.decor} x="186" y="30" width="24" height="16" />
        <rect className={styles.decor} x="216" y="30" width="24" height="16" />
        <rect className={styles.decor} x="246" y="30" width="24" height="16" />
        <rect className={styles.decor} x="276" y="30" width="24" height="16" />
        <rect className={styles.decor} x="196" y="70" width="96" height="16" />
        <rect className={styles.decor} x="196" y="150" width="130" height="16" />
        <rect className={styles.chest} x="300" y="172" width="26" height="14" />
        <text className={styles.roomLabel} x="262" y="122">실험실</text>
        {people('lab', 255, 140)}
        <text className={styles.tiny} x="363" y="196" textAnchor="middle">뒷문</text>
      </g>

      <g {...room('cold')}>
        <rect className={styles.coldWall} x="314" y="24" width="90" height="78" />
        <line className={styles.decorLine} x1="324" y1="36" x2="394" y2="36" />
        <line className={styles.decorLine} x1="324" y1="46" x2="394" y2="46" />
        <text className={styles.roomLabel} x="359" y="70">저온실</text>
        <text className={styles.tiny} x="359" y="88" textAnchor="middle">4°C</text>
      </g>

      <g {...room('prof')}>
        <rect x="404" y="24" width="120" height="180" />
        <rect className={styles.decor} x="440" y="46" width="60" height="22" />
        <circle className={styles.decor} cx="470" cy="82" r="7" />
        <text className={styles.roomLabel} x="464" y="130">교수실</text>
      </g>

      <g {...room('inst')}>
        <rect x="24" y="244" width="200" height="72" />
        <rect className={styles.decor} x="40" y="258" width="64" height="30" rx="6" />
        <circle className={styles.decor} cx="60" cy="273" r="6" />
        <circle className={styles.decor} cx="84" cy="273" r="6" />
        <text className={styles.roomLabel} x="150" y="276">기기실</text>
        <text className={styles.tiny} x="150" y="292" textAnchor="middle">414호</text>
        {people('inst', 150, 304)}
      </g>

      {/* doors */}
      <line className={styles.door} x1="92" y1="204" x2="118" y2="204" />
      <line className={styles.door} x1="174" y1="104" x2="174" y2="130" />
      <line className={styles.door} x1="350" y1="204" x2="376" y2="204" />
      <line className={styles.door} x1="336" y1="102" x2="360" y2="102" />
      <line className={styles.door} x1="404" y1="150" x2="404" y2="176" />
      <line className={styles.door} x1="112" y1="244" x2="138" y2="244" />

      <rect className={styles.shaft} x="316" y="244" width="88" height="72" />
      <text className={styles.tiny} x="360" y="284" textAnchor="middle">화물 승강기</text>
      <rect className={styles.shaft} x="604" y="244" width="92" height="72" />
      <text className={styles.tiny} x="650" y="284" textAnchor="middle">계단</text>
      <path className={styles.stairLink} d="M650 244 V176" />

      <text className={styles.floorTag} x="580" y="38">1층</text>
      <g {...room('guardroom')}>
        <rect x="580" y="46" width="116" height="130" />
        <rect className={styles.decor} x="592" y="58" width="16" height="11" />
        <rect className={styles.decor} x="612" y="58" width="16" height="11" />
        <rect className={styles.decor} x="632" y="58" width="16" height="11" />
        <rect className={styles.decor} x="652" y="58" width="16" height="11" />
        <rect className={styles.alarmPanel} x="674" y="58" width="12" height="24" />
        <text className={styles.roomLabel} x="638" y="118">경비실</text>
        {people('guardroom', 638, 140)}
      </g>
    </svg>
  );
}

export function MillSiteMap(props) {
  const { room, people, outside } = mapHelpers(props);

  return (
    <svg className={styles.map} viewBox="0 0 720 340" role="group" aria-label="대성제분 배치도">
      <text className={styles.floorTag} x="20" y="15">대성제분</text>
      <rect className={styles.shaft} x="20" y="24" width="444" height="296" />

      <g {...room('warehouse')}>
        <path d="M36 40 H196 V100 H266 V180 H36 Z" />
        <ellipse className={styles.burn} cx="74" cy="104" rx="42" ry="34" />
        <ellipse className={styles.burn} cx="214" cy="118" rx="40" ry="26" />
        <rect className={styles.decor} x="36" y="92" width="7" height="24" />
        <text className={styles.tiny} x="50" y="108">분전반</text>
        <text className={styles.roomLabel} x="142" y="140">제품 창고</text>
        {people('warehouse', 136, 158)}
      </g>

      <g {...room('office')}>
        <rect x="196" y="40" width="70" height="60" />
        <ellipse className={styles.burn} cx="231" cy="70" rx="26" ry="20" />
        <text className={styles.roomLabel} x="231" y="76">사무실</text>
      </g>

      <g {...room('admin')}>
        <rect x="304" y="40" width="144" height="84" />
        <rect className={styles.decor} x="316" y="50" width="30" height="14" />
        <rect className={styles.decor} x="352" y="50" width="30" height="14" />
        <text className={styles.roomLabel} x="376" y="92">사무동</text>
        {people('admin', 364, 110)}
      </g>

      <g {...room('mill')}>
        <rect x="304" y="152" width="144" height="122" />
        <circle className={styles.decor} cx="424" cy="176" r="13" />
        <circle className={styles.decor} cx="394" cy="176" r="13" />
        <rect className={styles.decor} x="316" y="164" width="30" height="20" />
        <text className={styles.roomLabel} x="376" y="224">제분동</text>
        {people('mill', 370, 244)}
      </g>

      <g {...room('gate')}>
        <rect x="36" y="236" width="104" height="66" />
        <rect className={styles.alarmPanel} x="120" y="246" width="10" height="20" />
        <text className={styles.roomLabel} x="88" y="276">경비실</text>
      </g>

      {/* the gate the camera watches, and the side gate it does not */}
      <line className={styles.door} x1="170" y1="320" x2="230" y2="320" />
      <text className={styles.tiny} x="200" y="310" textAnchor="middle">정문</text>
      <path className={styles.cameraCone} d="M142 300 L172 334 L232 334 Z" />
      <line className={styles.door} x1="464" y1="286" x2="464" y2="310" />
      <text className={styles.tiny} x="456" y="302" textAnchor="end">쪽문</text>

      <text className={styles.floorTag} x="492" y="15">공장 밖</text>
      {outside('station', 24)}
      {outside('home', 98)}
      {outside('agency', 172)}
      {outside('gosiwon', 246)}
    </svg>
  );
}

export function PensionMap(props) {
  const { room, people, outside } = mapHelpers(props);
  const { isOpen } = props;

  return (
    <svg className={styles.map} viewBox="0 0 720 340" role="group" aria-label="파도소리 펜션 배치도">
      <text className={styles.floorTag} x="20" y="15">파도소리 펜션</text>
      <rect className={styles.shaft} x="20" y="24" width="444" height="296" />

      {/* the loft sits under the roof, open to the stairs below */}
      <g {...room('loft')}>
        <path d="M64 102 L92 44 H212 L240 102 Z" />
        <rect className={styles.decor} x="120" y="58" width="50" height="20" />
        <text className={styles.roomLabel} x="152" y="96">다락</text>
      </g>

      <g {...room('house')}>
        <rect x="40" y="106" width="222" height="110" />
        <line className={styles.decorLine} x1="112" y1="106" x2="112" y2="216" />
        <line className={styles.decorLine} x1="196" y1="106" x2="196" y2="216" />
        <text className={styles.tiny} x="76" y="206" textAnchor="middle">안방</text>
        <text className={styles.tiny} x="154" y="206" textAnchor="middle">거실</text>
        <text className={styles.tiny} x="229" y="206" textAnchor="middle">작은방</text>
        <text className={styles.roomLabel} x="151" y="156">해송동 1층</text>
      </g>

      <g {...room('boiler')}>
        <rect x="262" y="134" width="108" height="82" />
        <rect className={styles.alarmPanel} x="346" y="142" width="14" height="22" />
        <text className={styles.roomLabel} x="316" y="184">보일러실</text>
        {people('boiler', 309, 202)}
      </g>

      <g {...room('lodge')}>
        <rect x="350" y="40" width="100" height="70" />
        <text className={styles.roomLabel} x="400" y="72">관리동</text>
        {people('lodge', 394, 92)}
      </g>

      {/* the back door between the house and the boiler room */}
      <line className={styles.door} x1="262" y1="166" x2="262" y2="190" />

      <path className={styles.stairLink} d="M300 236 V224 M150 236 H300" />
      <g {...room('parking')}>
        <rect x="300" y="236" width="152" height="62" />
        <text className={styles.roomLabel} x="376" y="266">{isOpen('parking') ? '공영주차장' : '· · ·'}</text>
        <text className={styles.tiny} x="376" y="286" textAnchor="middle">{isOpen('parking') ? '300m' : ''}</text>
      </g>

      <path className={styles.decorLine} fill="none" d="M34 292 q9 -8 18 0 t18 0 t18 0 t18 0 t18 0 t18 0 t18 0 t18 0 t18 0 t18 0 t18 0 t18 0" />
      <text className={styles.tiny} x="40" y="312">모항 해변</text>

      <text className={styles.floorTag} x="492" y="15">펜션 밖</text>
      {outside('hospital', 24)}
      {outside('station', 98)}
      {outside('museum', 172)}
    </svg>
  );
}

export function NationMap(props) {
  const { outside, node } = mapHelpers(props);

  return (
    <svg className={styles.map} viewBox="0 0 720 400" role="group" aria-label="사건 지도">
      <text className={styles.floorTag} x="20" y="15">전국</text>
      <rect className={styles.shaft} x="20" y="24" width="444" height="356" />

      {/* every trip starts from the office in the middle */}
      <path
        className={styles.stairLink}
        d="M214 172 L128 100 M248 172 L248 100 M316 186 L346 172 M196 226 L128 244 M300 226 L368 244 M290 226 L404 350"
      />

      <text className={styles.tiny} x="36" y="40">서울</text>
      {node('bereaved', 36, 46)}
      {node('hansung', 180, 46)}

      <text className={styles.tiny} x="324" y="112">강원</text>
      {node('hongcheon', 324, 118)}

      {node('hq', 180, 172)}

      <text className={styles.tiny} x="36" y="238">경기</text>
      {node('hwaseong', 36, 244)}
      {node('nfs', 324, 244)}

      <circle className={styles.decor} cx="408" cy="354" r="5" />
      <text className={styles.tiny} x="420" y="358">부산</text>

      <text className={styles.floorTag} x="492" y="15">가방 속</text>
      {outside('phone', 24)}
    </svg>
  );
}

export function FinalMap(props) {
  const { outside, node } = mapHelpers(props);
  const { isOpen } = props;
  // Long place names get a short label in the box and the rest in small type above it.
  const tag = (id, x, y, text) => (
    <text className={styles.tiny} x={x} y={y}>{isOpen(id) ? text : ''}</text>
  );

  return (
    <svg className={styles.map} viewBox="0 0 720 340" role="group" aria-label="사건 지도">
      <text className={styles.floorTag} x="20" y="15">12월</text>
      <rect className={styles.shaft} x="20" y="24" width="444" height="296" />

      {/* every trip starts from the office; the two basements hang off the head office */}
      <path
        className={styles.stairLink}
        d="M232 140 L96 98 M240 140 L240 98 M248 140 L384 98 M232 194 L96 228 M240 194 L240 228 M308 246 L316 222 M308 262 L316 288"
      />

      {node('nfs', 28, 44)}
      {node('bereaved', 172, 44)}
      {node('vault', 316, 44)}

      {node('hq', 172, 140)}

      {tag('incheon', 28, 222, '인천')}
      {node('incheon', 28, 228, '신물류센터')}
      {node('hansung', 172, 228)}
      {tag('hall', 316, 188, '지하 2층')}
      {node('hall', 316, 194, '컨벤션홀')}
      {tag('control', 316, 256, '지하 1층')}
      {node('control', 316, 262, '방재실')}

      <text className={styles.floorTag} x="492" y="15">주머니 속</text>
      {outside('phone', 24)}
      {outside('home', 98)}
    </svg>
  );
}
